import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import Paginator from '../paginator';
import { apiurl } from '../../utils/constants';
import Highcharts from 'highcharts';
import drilldown from 'highcharts/modules/drilldown';
import HighchartsReact from 'highcharts-react-official';
import { localDateToUtcSql } from '../../utils/functions';
drilldown(Highcharts);

// Gráfico de cambios de estado por mes (columnas apiladas), análogo al de listStat.js.
// Tiene su propia query (independiente de /invoices, que filtra por DocDate >= initDate):
// trae las facturas cuyo TaxDate/DocDate/approved/paid caiga en el rango, para no perder
// facturas contabilizadas antes del rango pero pagadas dentro de él (o viceversa).
function StateChangesByMonth({ mail, sapDB, budg, iuser, from = '', to = '', selectedCent = false }) {

	// Colores de estado usados en la app (--ora, --yel, --gre, --blu)
	const STATE_COLORS = { Emitida: '#F60', Contabilizada: '#FC0', Aprobada: '#0A2', Pagada: '#06B' };
	const STATES = ['Emitida', 'Contabilizada', 'Aprobada', 'Pagada'];
	const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

	const [data, setData] = useState([]);

	useEffect(() => {
		let cancelled = false;

		const load = async () => {
			try {
				const utcFrom = from ? localDateToUtcSql(from, '00:00:00') : '';
				const utcTo = to ? localDateToUtcSql(to, '23:59:59') : '';

				// Nota: no se reutiliza el filtro de "pendientes de aprobar" de /invoices
				// (exige approved IS NULL), porque este gráfico necesita ver también las
				// facturas ya aprobadas/pagadas, no solo las pendientes.
				// Excluir borradores debe aplicar siempre, sin importar budg.
				let where = `AND form.requester IS NOT NULL AND form.docu IS NOT NULL`;
				if (!budg && iuser) {
					where += ` AND (cent.bill=${iuser} OR form.approver=${iuser})`;
				}

				// TaxDate/DocDate son fecha de calendario (día), sin hora/timezone relevante:
				// se comparan por DATE() contra el día local tal cual, sin convertir a UTC.
				// DocDate es DATETIME (normalmente en 00:00:00, pero por seguridad se le saca
				// la hora con DATE() para que nunca quede fuera de rango por eso).
				// approved/paid son DATETIME (instante real): sí se convierten a UTC.
				const dateCols = [
					{ c: 'TaxDate', from: from, to: to, pure: true },
					{ c: 'DocDate', from: from, to: to, pure: true },
					{ c: 'approved', from: utcFrom, to: utcTo, pure: false },
					{ c: 'paid', from: utcFrom, to: utcTo, pure: false },
				];
				const inRange = ({ c, from: f, to: t, pure }) => {
					const col = pure ? `DATE(form.${c})` : `form.${c}`;
					return [
						f && `${col} >= '${f}'`,
						t && `${col} <= '${t}'`,
					].filter(Boolean).join(' AND ');
				};
				const dateFilter = (from || to)
					? `AND ( ${dateCols.map(dc => `(${inRange(dc)})`).join(' OR ')} )`
					: '';

				const query = `
					SELECT form.iden, form.TaxDate, form.DocDate, form.approved, form.paid, cent.name cc_name
					FROM bil__form form
					LEFT JOIN req__cent cent ON form.cent = cent.code
					WHERE 1
					${where}
					${dateFilter}
					ORDER BY form.iden DESC;
				`;
				console.log('StateChangesByMonth query', query);

				const response = await fetch(apiurl + 'queryAux', {
					method: 'POST',
					credentials: 'include',
					headers: { 'Content-Type': 'application/json' },
					body: JSON.stringify({ user: mail, query })
				});

				if (response.ok && !cancelled) {
					const rows = await response.json();
					setData(Array.isArray(rows) ? rows : []);
				}
			} catch (error) {
				console.log('Error en StateChangesByMonth load ', error);
			}
		};

		// debounce: si el usuario sigue cambiando la fecha, no dispara la query en cada tecla
		const timer = setTimeout(load, 500);
		return () => { cancelled = true; clearTimeout(timer); };
	}, [mail, sapDB, budg, iuser, from, to]);

	const options = useMemo(() => {
		const rows = selectedCent ? data.filter(inv => inv.cc_name === selectedCent) : data;
		const buckets = {};
		// TaxDate/DocDate son DATE puras: se toma el día tal cual viene en el string
		// (sin pasar por `new Date()`, que las interpretaría como medianoche UTC y
		// las correría un día hacia atrás en Chile). approved/paid sí son instantes
		// reales y usan `new Date()` para obtener el día en la zona local.
		const pureDay = (dateStr) => {
			const m = String(dateStr).match(/^(\d{4})-(\d{2})-(\d{2})/);
			return m ? `${m[1]}-${m[2]}-${m[3]}` : null;
		};
		const instantDay = (dateStr) => {
			const d = new Date(dateStr);
			if (isNaN(d.getTime())) return null;
			return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
		};

		const add = (dateStr, state, dayFn) => {
			if (!dateStr) return;
			const day = dayFn(dateStr);
			if (!day) return;
			// Solo cuenta la acción si ocurrió dentro del rango filtrado
			if ((from && day < from) || (to && day > to)) return;
			const k = day.slice(0, 7);
			if (!buckets[k]) buckets[k] = { Emitida: 0, Contabilizada: 0, Aprobada: 0, Pagada: 0 };
			buckets[k][state]++;
		};

		rows.forEach(inv => {
			add(inv.TaxDate, 'Emitida', pureDay);
			add(inv.DocDate, 'Contabilizada', pureDay);
			add(inv.approved, 'Aprobada', instantDay);
			add(inv.paid, 'Pagada', instantDay);
		});

		const keys = Object.keys(buckets).sort();
		const sameYear = new Set(keys.map(k => k.split('-')[0])).size <= 1;
		const categories = keys.map(k => {
			const [y, m] = k.split('-');
			return sameYear ? MONTHS[Number(m) - 1] : `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Cambios de estado por mes' },
			xAxis: { categories },
			yAxis: { min: 0, title: { text: 'Facturas' } },
			legend: { reversed: true },
			plotOptions: { column: { stacking: 'normal' } },
			tooltip: {
				headerFormat: '<b>{point.x}</b><br/>',
				pointFormat: '{series.name}: {point.y}<br/>Total: {point.stackTotal}'
			},
			series: STATES.map(st => ({
				name: st,
				color: STATE_COLORS[st],
				data: keys.map(k => buckets[k][st]),
			})),
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data, selectedCent, from, to]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

function fmtCLP(v) {
	const n = Number(v) || 0;
	const abs = Math.abs(n);
	const fmt = (x, suf) => `$${x.toLocaleString('es-CL', { maximumFractionDigits: 1 })}${suf}`;
	if (abs >= 1e9) return fmt(n / 1e9, 'B');
	if (abs >= 1e6) return fmt(n / 1e6, 'M');
	if (abs >= 1e3) return fmt(n / 1e3, 'K');
	return fmt(n, '');
}

// Gasto mensual: suma de DocTotal de cada factura por mes (fecha DocDate), análogo al de listStat.js.
// El azul de la app (--blu) se aclara según el tamaño de la barra.
function MonthlySpend({ rows = [] }) {

	const MONTHS = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];

	const options = useMemo(() => {
		// Azul definido en el CSS de la app (--blu); expande #rgb -> #rrggbb
		let blu = (getComputedStyle(document.documentElement).getPropertyValue('--blu')
			|| getComputedStyle(document.body).getPropertyValue('--blu')).trim();
		if (/^#[0-9a-f]{3}$/i.test(blu)) blu = '#' + blu.slice(1).split('').map(c => c + c).join('');

		const buckets = {};
		rows.forEach(inv => {
			if (inv.DocumentStatus === 'Cancelled' || inv.DocumentStatus === 'Rejected') return;
			if (!inv.DocDate) return;
			// DocDate es fecha de calendario (sin hora significativa): se agrupa por
			// texto, sin pasar por new Date() (que la interpretaría como UTC y podría
			// correrla de mes en Chile).
			const m = String(inv.DocDate).match(/^(\d{4})-(\d{2})/);
			if (!m) return;
			const k = `${m[1]}-${m[2]}`;
			buckets[k] = (buckets[k] || 0) + (Number(inv.DocTotal) || 0);
		});

		const keys = Object.keys(buckets).sort();
		const max = Math.max(1, ...keys.map(k => buckets[k]));

		const points = keys.map(k => {
			const v = buckets[k];
			const ratio = v / max;
			return { y: v, color: Highcharts.color(blu).brighten(0.55 * (1 - ratio)).get('rgb') };
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Gasto mensual' },
			xAxis: {
				categories: (() => {
					const sameYear = new Set(keys.map(k => k.split('-')[0])).size <= 1;
					return keys.map(k => {
						const [y, m] = k.split('-');
						return sameYear ? MONTHS[Number(m) - 1] : `${MONTHS[Number(m) - 1]} ${y.slice(2)}`;
					});
				})()
			},
			yAxis: {
				min: 0,
				title: { text: null },
				labels: { formatter() { return fmtCLP(this.value); } },
			},
			legend: { enabled: false },
			tooltip: { pointFormatter() { return `<b>${fmtCLP(this.y)}</b>`; } },
			series: [{ name: 'Gasto', data: points }],
		};
	}, [rows]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Tiempo entre estados (días promedio), análogo al de listStat.js.
function TimeBetweenStates({ rows = [] }) {

	const options = useMemo(() => {
		const diffDays = (end, start) => {
			if (!end || !start) return null;
			const e = new Date(end), s = new Date(start);
			if (isNaN(e.getTime()) || isNaN(s.getTime())) return null;
			const d = (e - s) / 86400000;
			return d >= 0 ? d : null;
		};

		const avg = (arr) => arr.length
			? Math.round((arr.reduce((a, b) => a + b, 0) / arr.length) * 10) / 10
			: 0;

		const cont = [], appr = [], paid = [];
		rows.forEach(inv => {
			const dCont = diffDays(inv.DocDate, inv.TaxDate);
			if (dCont !== null) cont.push(dCont);

			const dAppr = diffDays(inv.approved, inv.DocDate);
			if (dAppr !== null) appr.push(dAppr);

			const dPaid = diffDays(inv.paid, inv.approved);
			if (dPaid !== null) paid.push(dPaid);
		});

		return {
			chart: { type: 'column', height: 340 },
			title: { text: 'Tiempo entre estados' },
			xAxis: { categories: ['Contabilización', 'Aprobación', 'Pago'] },
			yAxis: { min: 0, title: { text: 'Días promedio' } },
			legend: { enabled: false },
			tooltip: { pointFormat: '<b>{point.y} días</b> (promedio)' },
			plotOptions: { column: { colorByPoint: true } },
			colors: ['#FC0', '#0A2', '#06B'],
			series: [{
				name: 'Días promedio',
				data: [avg(cont), avg(appr), avg(paid)],
			}],
		};
	}, [rows]);

	return <HighchartsReact highcharts={Highcharts} options={options} />;
}

// Estadísticas de Facturas. Misma estructura de diseño que listStat.js
// (main > nav > section/article, aside con Acciones y Filtros).
function ListStatInvoice({ onMenuItemClick }) {

	const { iden: iuser, mail, sapDB, budg } = useSelector((state) => state.user);

	const [invoices, setInvoices] = useState([]);
	const now = new Date();
	const firstDayOfMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
	const [filtDateStart, setFiltDateStart] = useState(firstDayOfMonth);
	const [filtDateEnd, setFiltDateEnd] = useState(false);
	const [paginatedInvoices, setPaginatedInvoices] = useState([]);
	const [currentPage, setCurrentPage] = useState(1);
	const [totalFilteredInvoices, setTotalFilteredInvoices] = useState(0);
	const [selectedState, setSelectedState] = useState(null);
	const [selectedCent, setSelectedCent] = useState(false);
	const itemsPerPage = 10;
	const drilledRef = useRef(false);
	const [options, setOptions] = useState({
		chart: { type: 'pie' },
		title: { text: 'Estado de facturas' }
	});

	const loadInvoices = useCallback(async () => {
		try {
			const response = await fetch(apiurl + 'invoices', {
				method: 'POST',
				credentials: 'include',
				headers: { 'Content-Type': 'application/json' },
				// DocDate es fecha de calendario (sin hora significativa): se filtra con
				// el día local tal cual, sin convertir a UTC (ver ORM-645).
				body: JSON.stringify({ sapDB, user: mail, budg, iuser, initDate: filtDateStart || undefined })
			});

			if (response.ok) {
				const data = await response.json();
				setInvoices(Array.isArray(data) ? data : []);
			}
		} catch (error) {
			console.log('Error en loadInvoices ', error);
		}
	}, [budg, iuser, mail, sapDB, filtDateStart]);

	useEffect(() => {
		const timer = setTimeout(loadInvoices, 500);
		return () => clearTimeout(timer);
	}, [loadInvoices]);

	// Una factura es "Cerrada" si fue rechazada, cancelada o cerrada en SAP;
	// cualquier otro estado (Borrador, Emitida, Aprobada, Pagada, Contabilizada...) es "Pendiente".
	const closedSubstate = (inv) => {
		if (inv.Cancelled === 'C' || inv.DocumentStatus === 'Cancelled') return 'Cancelada';
		if (inv.DocumentStatus === 'Rejected') return 'Rechazada';
		return 'Cerrada';
	};
	const isClosed = (inv) => inv.Cancelled === 'C' || ['Cancelled', 'Rejected', 'Closed', 'C'].includes(inv.DocumentStatus);

	// Colores de estado usados en la app (--ora, --yel, --gre, --blu)
	const PENDIENTE_COLORS = { Emitida: '#F60', Contabilizada: '#FC0', Aprobada: '#0A2', Pagada: '#06B' };
	const pendienteSubstate = (inv) => {
		if (inv.DocumentStatus === 'Issued') return 'Emitida';
		if (inv.DocumentStatus === 'Approved') return 'Aprobada';
		if (inv.DocumentStatus === 'Paid') return 'Pagada';
		return 'Contabilizada';
	};
	const pendienteSubestados = ['Emitida', 'Contabilizada', 'Aprobada', 'Pagada'];
	const cerradaSubestados = ['Rechazada', 'Cancelada', 'Cerrada'];

	// Los borradores no son facturas emitidas todavía; no entran en la estadística
	const filteredInvoices = useMemo(() => invoices
		.filter(inv => inv.DocumentStatus !== 'Draft')
		// DocDate es fecha de calendario: se compara como texto (YYYY-MM-DD), sin
		// pasar por new Date() ni convertir a UTC (ver ORM-645).
		.filter(inv => (!filtDateEnd || !inv.DocDate || String(inv.DocDate).slice(0, 10) <= filtDateEnd)),
	[invoices, filtDateEnd]);

	// Filtro de centro de costos (clic en la barra del panel "Centro de costos")
	const ccFilteredInvoices = useMemo(() => (
		selectedCent ? filteredInvoices.filter(inv => inv.cc_name === selectedCent) : filteredInvoices
	), [filteredInvoices, selectedCent]);

	// Aplica el filtro elegido en el gráfico (clic en Pendiente/Cerrada, o en un sub-estado)
	const stateFilteredInvoices = useMemo(() => {
		if (!selectedState) return ccFilteredInvoices;
		if (selectedState === 'Pendiente') return ccFilteredInvoices.filter(inv => !isClosed(inv));
		if (selectedState === 'Cerrada') return ccFilteredInvoices.filter(isClosed);
		if (pendienteSubestados.includes(selectedState)) {
			return ccFilteredInvoices.filter(inv => !isClosed(inv) && pendienteSubstate(inv) === selectedState);
		}
		if (cerradaSubestados.includes(selectedState)) {
			return ccFilteredInvoices.filter(inv => isClosed(inv) && closedSubstate(inv) === selectedState);
		}
		return ccFilteredInvoices;
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ccFilteredInvoices, selectedState]);

	// Panel "Centro de costos": % de facturas pendientes por CC (no depende de selectedCent,
	// para poder seguir viendo/alternando todas las barras).
	const centStats = useMemo(() => {
		const groups = {};
		filteredInvoices.forEach(inv => {
			const name = inv.cc_name || 'Sin centro de costo';
			if (!groups[name]) groups[name] = { cent_name: name, total: 0, abiertas: 0 };
			groups[name].total++;
			if (!isClosed(inv)) groups[name].abiertas++;
		});
		return Object.values(groups)
			.map(g => {
				const cerradas = g.total - g.abiertas;
				return { ...g, cerradas, porcentaje: g.total ? cerradas / g.total : 0 };
			})
			.sort((a, b) => b.porcentaje - a.porcentaje)
			.slice(0, 20);
	}, [filteredInvoices]);

	useEffect(() => {
		setTotalFilteredInvoices(stateFilteredInvoices.length);
		const startIndex = (currentPage - 1) * itemsPerPage;
		setPaginatedInvoices(stateFilteredInvoices.slice(startIndex, startIndex + itemsPerPage));
	}, [stateFilteredInvoices, currentPage]);

	useEffect(() => {
		setCurrentPage(1);
	}, [selectedState]);

	const setHighChartOption = useCallback(() => {
		const filtered = ccFilteredInvoices;

		const pendientes = filtered.filter(inv => !isClosed(inv));
		const cerradas = filtered.filter(isClosed);

		const pendienteData = {
			name: 'Pendiente',
			y: pendientes.length,
			color: '#09F',
			drilldown: 'Pendientes',
		};

		const pendientesDrilldownData = {
			id: 'Pendientes',
			name: 'Pendientes',
			data: pendienteSubestados.map(estado => ({
				name: estado,
				y: pendientes.filter(inv => pendienteSubstate(inv) === estado).length,
				color: PENDIENTE_COLORS[estado],
			})),
			events: {
				click: function (event) { setSelectedState(event.point.name); }
			}
		};

		const cerradaMainData = {
			name: 'Cerrada',
			y: cerradas.length,
			color: '#666',
			drilldown: 'Cerradas',
		};

		const cerradasDrilldownData = {
			id: 'Cerradas',
			name: 'Cerradas',
			data: cerradaSubestados.map(estado => ({
				name: estado,
				y: cerradas.filter(inv => closedSubstate(inv) === estado).length,
				color: (estado === 'Rechazada' ? '#C00' : (estado === 'Cancelada' ? '#666' : '#0A2')),
			})),
			events: {
				click: function (event) { setSelectedState(event.point.name); }
			}
		};

		const charOptions = {
			chart: {
				type: 'pie',
				height: 340,
				events: {
					drilldown: function () { drilledRef.current = true; },
					// Al volver al estado raíz (root/padre) se limpia el filtro de la tabla
					drillup: function () { drilledRef.current = false; setSelectedState(null); },
					load: function () {
						const chart = this;
						if (drilledRef.current) {
							setTimeout(() => {
								const point = chart.series[0] && chart.series[0].points.find(p => p.drilldown);
								if (point) {
									if (point.runDrilldown) point.runDrilldown();
									else point.doDrilldown();
								}
							}, 0);
						}
					}
				}
			},
			title: { text: 'Estado de facturas' },
			credits: { enabled: false },
			series: [{
				name: 'Estados',
				data: [pendienteData, cerradaMainData],
				size: '100%',
				events: {
					click: function (event) { setSelectedState(event.point.name); }
				}
			}],
			drilldown: {
				series: [pendientesDrilldownData, cerradasDrilldownData],
			},
			tooltip: {
				headerFormat: '',
				pointFormat: '<b>{point.name}</b>: {point.y}<br/>'
			},
			plotOptions: {
				pie: {
					cursor: 'pointer',
					dataLabels: {
						enabled: true,
						format: '{point.name}: {point.y}',
						distance: -50,
						style: {
							color: 'white',
							textOutline: 'none',
							textDecoration: 'none'
						}
					},
					showInLegend: false
				}
			}
		};

		setOptions(charOptions);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [ccFilteredInvoices]);

	useEffect(() => {
		setHighChartOption();
	}, [setHighChartOption]);

	return (
		<>
			<style>{
				`g.highcharts-data-labels.highcharts-series-0.highcharts-pie-series.highcharts-tracker > g.highcharts-label.highcharts-data-label.highcharts-drilldown-data-label > text {
					color: white !important;
					fill: white !important;
					text-decoration: none !important;
					border: 1px solid black !important;
				}
				.highcharts-credits{
					display: none !important;
				}
			`}</style>
			<form>
				<main>
					<h1 data-icon=''>Estadísticas de Facturas</h1>
					<nav>
						<section>
							<article>
								{options.series && <HighchartsReact key={selectedCent} highcharts={Highcharts} options={options} />}
							</article>
						</section>
						<section>
							<article>
								<StateChangesByMonth mail={mail} sapDB={sapDB} budg={budg} iuser={iuser} from={filtDateStart || ''} to={filtDateEnd || ''} selectedCent={selectedCent} />
							</article>
						</section>
						<section>
							<article>
								<TimeBetweenStates rows={ccFilteredInvoices} />
							</article>
						</section>
					</nav>
					<nav>
						<section className="wide">
							<article>
								<h2>
									<div>Facturas</div>
								</h2>
								<table>
									<tbody>
										{
											paginatedInvoices.map((invoice, index) => (
												<tr key={index}>
													<td>{invoice.FolioNum ? `${invoice.FolioPref}-` : ''}{invoice.FolioNum ?? '-'}</td>
													<td>{invoice.CardCode}</td>
													<td>{invoice.DocDate ? invoice.DocDate.slice(0, 10).split('-').reverse().join('/') : '-'}</td>
												</tr>))
										}
									</tbody>
								</table>
								<Paginator totalItems={totalFilteredInvoices} itemsPerPage={itemsPerPage} currentPage={currentPage} onPageChange={setCurrentPage} />
							</article>
						</section>
						<section>
							<article>
								<MonthlySpend rows={ccFilteredInvoices} />
							</article>
						</section>
					</nav>
				</main>
				<aside>
					<article>
						<h2>Acciones </h2>
						<fieldset>
							<input type="button" value="Volver a facturas" onClick={() => onMenuItemClick('listInvoice')} />
						</fieldset>
					</article>
					<article>
						<h2>Filtros </h2>
						<fieldset>
							<legend>Contabilización</legend>
							<input type="date" value={filtDateStart} onChange={(e) => { setFiltDateStart(e.target.value); }} />
							<input type="date" value={filtDateEnd} onChange={(e) => { setFiltDateEnd(e.target.value); }} />
						</fieldset>
					</article>
					<article>
						<h2>Centro de costos</h2>
						<fieldset>
							{
								centStats.map((c) => (
									<progress
										key={c.cent_name}
										className={selectedCent === c.cent_name ? 'sel' : ''}
										value={c.cerradas}
										max={c.total}
										data-label={c.cent_name}
										data-value={`${c.abiertas}/${c.cerradas}`}
										onClick={() => { setSelectedCent(prev => prev === c.cent_name ? false : c.cent_name); }}
									></progress>
								))
							}
						</fieldset>
					</article>
				</aside>
			</form>
		</>
	);
}

export default ListStatInvoice;
